/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { DocumentsFormConfig } from "../../../services/documentsFormService";
import Grid from "@mui/material/Grid";
import wizardStyle from "../wizardStyle";
import Typography from "@mui/material/Typography";
import MultipleFileUpload from "../Common/FileUpload/MultipleFileUpload";
import { filterMediaObjectsByFieldname } from "../../../services/mediaObjects";
import { MissingDocumentCheckbox } from "./MissingDocumentCheckbox";
import FileUploadSection from "../Common/FileUploadSection/FileUploadSection";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import useMediaObjectCombinationRequest from "../../../hooks/useMediaObjectCombinationRequest";
import { MediaObject } from "../../../types/Entities/MediaObject/MediaObject";
import React from "react";
import { useForm } from "../../../provider/Form/FormProvider";
import { isRequired } from "../../../services/validationRules";

export type SectionElementsProps = {
  configs: DocumentsFormConfig[];
  product: AbstractCase;
  mediaObjectCollection: MediaObject[];
  onMissingDocumentsCheckboxChange: (documentType: string) => void;
  uploadIsBlocked: boolean;
  setUploadIsBlocked: React.Dispatch<React.SetStateAction<boolean>>;
  refreshMediaObjectCollection: () => void;
  refreshCase: () => void;
} & Omit<ReturnType<typeof useMediaObjectCombinationRequest>, "isLoadingMediaObjectCombinationRequest">;

export const SectionElements = ({
  configs,
  product,
  updateAction,
  mediaObjectCombinationRequests,
  hasAtLeastOneFileOfType,
  mediaObjectCollection,
  onMissingDocumentsCheckboxChange,
  uploadIsBlocked,
  setUploadIsBlocked,
  refreshMediaObjectCollection,
  refreshCase,
}: SectionElementsProps) => {
  const { values, updateValue } = useForm();

  return (
    <>
      {configs.map((config) => {
        if (!config) {
          return null;
        }

        switch (config.type) {
          case "MultipleFileUpload": {
            const mediaObjectTypeName = config.mediaObjectTypeName || config.fieldname;

            return (
              <React.Fragment key={mediaObjectTypeName}>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  {config.headline && <Typography variant="h5">{config.headline({ ...config, product })}</Typography>}
                  {config.subHeadline && <Typography>{config.subHeadline({ ...config, product })}</Typography>}
                  {!values?.missingDocuments?.includes(mediaObjectTypeName) && (
                    <MultipleFileUpload
                      {...config}
                      product={product}
                      description={config?.description({ ...config, product })}
                      fetchMediaObjects={refreshMediaObjectCollection}
                      mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, mediaObjectTypeName)}
                      disabled={uploadIsBlocked}
                    />
                  )}
                </Grid>
                {config.isRequired?.({ product }) && !filterMediaObjectsByFieldname(mediaObjectCollection, mediaObjectTypeName).length && (
                  <MissingDocumentCheckbox
                    product={product}
                    onChange={onMissingDocumentsCheckboxChange}
                    updateValues={updateValue}
                    documentType={mediaObjectTypeName}
                    values={values}
                  />
                )}
              </React.Fragment>
            );
          }
          default: {
            const mediaObjectTypeName = config.mediaObjectTypeName || config.fieldName;

            return (
              <React.Fragment key={mediaObjectTypeName}>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  {
                    <FileUploadSection
                      {...config}
                      product={product.name}
                      productEntity={product}
                      productEntityId={product.id}
                      headline={config.headline?.({ ...config, product })}
                      description={config.description?.({ ...config, product })}
                      helperText={config.helperText?.({ ...config, product })}
                      updateAction={updateAction}
                      setIsBlocked={setUploadIsBlocked}
                      isBlocked={uploadIsBlocked}
                      hideUploadButton={values?.missingDocuments?.includes(mediaObjectTypeName)}
                      mediaObjectCombinationRequest={mediaObjectCombinationRequests?.[mediaObjectTypeName]!}
                      postSubmitCallback={refreshCase}
                    />
                  }
                </Grid>
                {config.isRequired?.({ product }) && !hasAtLeastOneFileOfType(mediaObjectCombinationRequests, mediaObjectTypeName) && (
                  <MissingDocumentCheckbox
                    product={product}
                    onChange={onMissingDocumentsCheckboxChange}
                    updateValues={updateValue}
                    documentType={mediaObjectTypeName}
                    values={values}
                  />
                )}
              </React.Fragment>
            );
          }
        }
      })}
    </>
  );
};
